import React from 'react';
import { Card, CardBody, CardHeader, Spinner } from 'reactstrap';
import { useTranslator } from '@front-activerse/common-components';
import type { WearableBodySummary } from '@front-activerse/common-components';
import type { ApolloError } from '@apollo/client';
import type { DateRange } from '../types';
import MetricValue from './MetricValue';

interface BodyMetric {
  key: keyof WearableBodySummary;
  labelKey: string;
  unitKey?: string;
}

const BODY_METRICS: BodyMetric[] = [
  {
    key: 'weightKg',
    labelKey: 'wearablesDashboard.metric.weight',
    unitKey: 'wearablesDashboard.unit.kg',
  },
  {
    key: 'heightCm',
    labelKey: 'wearablesDashboard.metric.height',
    unitKey: 'wearablesDashboard.unit.cm',
  },
  {
    key: 'bodyFatPercent',
    labelKey: 'wearablesDashboard.metric.bodyFat',
    unitKey: 'wearablesDashboard.unit.percent',
  },
  {
    key: 'muscleMassKg',
    labelKey: 'wearablesDashboard.metric.muscleMass',
    unitKey: 'wearablesDashboard.unit.kg',
  },
  { key: 'bmi', labelKey: 'wearablesDashboard.metric.bmi' },
  { key: 'age', labelKey: 'wearablesDashboard.metric.age' },
  {
    key: 'restingHeartRateBpm',
    labelKey: 'wearablesDashboard.metric.restingHr',
    unitKey: 'wearablesDashboard.unit.bpm',
  },
  {
    key: 'avgHrvSdnnMs',
    labelKey: 'wearablesDashboard.metric.hrvAvg',
    unitKey: 'wearablesDashboard.unit.ms',
  },
  {
    key: 'avgPeriodDays',
    labelKey: 'wearablesDashboard.metric.periodDays',
    unitKey: 'wearablesDashboard.unit.days',
  },
  {
    key: 'bodyTemperatureCelsius',
    labelKey: 'wearablesDashboard.metric.bodyTemp',
    unitKey: 'wearablesDashboard.unit.celsius',
  },
  {
    key: 'skinTemperatureCelsius',
    labelKey: 'wearablesDashboard.metric.skinTemp',
    unitKey: 'wearablesDashboard.unit.celsius',
  },
];

interface Props {
  range: DateRange;
  loading: boolean;
  error: ApolloError | undefined;
  bodySummary: WearableBodySummary | null;
}

const BodySummarySection: React.FC<Props> = ({ range, loading, error, bodySummary }) => {
  const { t } = useTranslator();

  return (
    <Card className="mb-4" data-testid="body-summary-section">
      <CardHeader>
        {t('wearablesDashboard.section.body.title')}
        {!range.isSingleDay && bodySummary ? (
          <small className="ms-2 text-muted">{t('wearablesDashboard.body.lastKnown')}</small>
        ) : null}
      </CardHeader>
      <CardBody>
        {loading && !bodySummary ? <Spinner /> : null}
        {error ? (
          <div data-testid="body-summary-error" className="text-danger">
            {t('wearablesDashboard.error.section')}: {error.message}
          </div>
        ) : null}
        {!loading && !error && !bodySummary ? (
          <div data-testid="body-summary-empty">{t('wearablesDashboard.empty.day')}</div>
        ) : null}
        {bodySummary
          ? BODY_METRICS.map((metric) => (
              <MetricValue
                key={metric.key as string}
                labelKey={metric.labelKey}
                unitKey={metric.unitKey}
                value={bodySummary[metric.key] as number | null}
                provider={bodySummary.provider}
                device={bodySummary.device}
              />
            ))
          : null}
      </CardBody>
    </Card>
  );
};

export default BodySummarySection;
export { BODY_METRICS };
